import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowRight,
  Books,
  Broadcast,
  CalendarBlank,
  Camera,
  CaretDown,
  ChartBar,
  CheckCircle,
  Exam,
  FunnelSimple,
  MinusCircle,
  MapPin,
  Monitor,
  PlayCircle,
  Target,
  Trophy,
  UsersThree,
  XCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtError } from "../lib/api";
import AcademicDomainChart from "../components/AcademicDomainChart";
import StudentLearningTimeline from "../components/StudentLearningTimeline";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const asAnswerOptions = (value) => {
  if (Array.isArray(value)) return value.map((item) => String(item || "").trim().toUpperCase()).filter(Boolean);
  const raw = String(value || "").trim().toUpperCase();
  if (!raw) return [];
  if (/^[A-E]+$/.test(raw)) return raw.split("");
  return raw.split(/[^A-E]+/).filter((item) => /^[A-E]$/.test(item));
};

export const answerOutcome = ({ position, selected, answerKey, annulled }) => {
  if (annulled.has(Number(position))) return "annulled";
  const expected = asAnswerOptions(answerKey?.[String(position)]);
  if (!expected.length) return "unscored";
  if (!selected) return "blank";
  return expected.includes(String(selected).trim().toUpperCase()) ? "correct" : "wrong";
};

const outcomeMeta = {
  correct: {
    label: "Acertou",
    icon: CheckCircle,
    chip: "border-[hsl(var(--success))] bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]",
  },
  wrong: {
    label: "Errou",
    icon: XCircle,
    chip: "border-destructive bg-destructive/10 text-destructive",
  },
  annulled: {
    label: "Anulada",
    icon: MinusCircle,
    chip: "border-border bg-card text-muted-foreground",
  },
  blank: {
    label: "Em branco",
    icon: MinusCircle,
    chip: "border-[hsl(var(--warning))]/60 bg-card text-[hsl(var(--warning))]",
  },
  unscored: {
    label: "Sem gabarito",
    icon: MinusCircle,
    chip: "border-border bg-card text-muted-foreground",
  },
};

const scoreTone = (score) => {
  if (Number(score) >= 70) return "text-[hsl(var(--success))]";
  if (Number(score) >= 50) return "text-[hsl(var(--warning))]";
  return "text-destructive";
};

const examDate = (examItem) => {
  const value = examItem.exam_date || examItem.created_at;
  if (!value) return "Data não informada";
  const safeValue = /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00` : value;
  const parsed = new Date(safeValue);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleDateString("pt-BR");
};

function StudentQuestionDialog({ selected, correction, onOpenChange }) {
  const position = selected?.position;
  const question = (correction?.exam?.question_items || []).find(
    (item) => Number(item.position) === Number(position),
  );
  const answerKey = correction?.exam?.answers || {};
  const selectedAnswer = String(correction?.answers?.[String(position)] || "").trim().toUpperCase();
  const expected = asAnswerOptions(answerKey[String(position)] || question?.correct_letters);
  const annulled = new Set((correction?.exam?.annulled || []).map(Number));
  const outcome = selected ? answerOutcome({ position, selected: selectedAnswer, answerKey, annulled }) : "unscored";
  const meta = outcomeMeta[outcome];
  const StatusIcon = meta.icon;

  return (
    <Dialog open={Boolean(selected)} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl font-black tracking-tight text-left">
            Questão {position}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2 border-b border-border pb-4">
          <span className={`inline-flex items-center gap-2 border px-3 py-2 text-xs font-mono-data font-bold uppercase tracking-wider ${meta.chip}`}>
            <StatusIcon size={16} weight="fill" /> {meta.label}
          </span>
          {question?.knowledge_area && <span className="border border-border px-3 py-2 text-xs">{question.knowledge_area}</span>}
          {question?.system_context && <span className="border border-border px-3 py-2 text-xs text-muted-foreground">{question.system_context}</span>}
        </div>

        {question?.stem ? (
          <div className="space-y-5">
            <div>
              {question.question_code && <div className="label-eyebrow mb-2">{question.question_code}</div>}
              <div className="text-base leading-7 whitespace-pre-wrap">{question.stem}</div>
            </div>

            {!!question.alternatives?.length && (
              <div className="space-y-3" data-testid="student-question-alternatives">
                {question.alternatives.map((alternative, index) => {
                  const letter = String(alternative.letter || String.fromCharCode(65 + index)).toUpperCase();
                  const isExpected = expected.includes(letter) || alternative.is_correct;
                  const isSelected = selectedAnswer === letter;
                  const tone = outcome === "annulled"
                    ? isSelected ? "border-foreground bg-muted/30" : "border-border"
                    : isExpected
                      ? "border-[hsl(var(--success))] bg-[hsl(var(--success))]/10"
                      : isSelected
                        ? "border-destructive bg-destructive/10"
                        : "border-border";
                  return (
                    <div key={`${letter}-${index}`} className={`border p-4 ${tone}`}>
                      <div className="flex gap-3">
                        <span className="font-mono-data font-black shrink-0">{letter}</span>
                        <div className="min-w-0 flex-1">
                          <div className="leading-6 whitespace-pre-wrap">{alternative.text || "Alternativa sem texto cadastrado"}</div>
                          <div className="flex flex-wrap gap-2 mt-2 text-[10px] font-mono-data font-bold uppercase tracking-wider">
                            {isSelected && <span>Sua resposta</span>}
                            {isExpected && outcome !== "annulled" && <span className="text-[hsl(var(--success))]">Resposta correta</span>}
                          </div>
                          {alternative.rationale && (
                            <div className="mt-3 pt-3 border-t border-current/10 text-sm text-muted-foreground leading-6 whitespace-pre-wrap">
                              {alternative.rationale}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="border border-dashed border-border p-6 text-sm text-muted-foreground">
            O enunciado desta questão não está disponível porque esta prova foi cadastrada somente com o gabarito.
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="border border-border p-4">
            <div className="label-eyebrow mb-2">sua resposta</div>
            <div className="font-display text-3xl font-black">{selectedAnswer || "—"}</div>
          </div>
          <div className="border border-border p-4">
            <div className="label-eyebrow mb-2">gabarito</div>
            <div className="font-display text-3xl font-black">{outcome === "annulled" ? "Anulada" : expected.join(" ou ") || "—"}</div>
          </div>
        </div>

        {question?.explanation && (
          <div className="border-l-4 border-primary bg-primary/5 p-5">
            <div className="label-eyebrow text-primary mb-2">correção comentada</div>
            <div className="text-sm leading-6 whitespace-pre-wrap">{question.explanation}</div>
          </div>
        )}
        {question && (
          <div className="border-t border-border pt-4 space-y-1 text-xs text-muted-foreground" data-testid="student-question-credits">
            <div><b>Autor editorial / banca:</b> {question.content_author_name || question.author_name || question.content_source_label || "—"}</div>
            <div><b>Fonte:</b> {question.source_reference || "—"}</div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AnswerGrid({ correction, onSelect }) {
  const count = Number(correction?.exam?.question_count || 0);
  const answerKeys = Object.keys(correction?.exam?.answers || {}).map(Number).filter(Number.isFinite);
  const answerPositions = Object.keys(correction?.answers || {}).map(Number).filter(Number.isFinite);
  const questionCount = count || Math.max(0, ...answerKeys, ...answerPositions);
  const answerKey = correction?.exam?.answers || {};
  const annulled = new Set((correction?.exam?.annulled || []).map(Number));

  return (
    <div>
      <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs mb-4" aria-label="Legenda das respostas">
        {[
          ["bg-[hsl(var(--success))]", "Acertou"],
          ["bg-destructive", "Errou"],
          ["bg-card border border-border", "Anulada"],
          ["bg-card border border-[hsl(var(--warning))]/60", "Em branco"],
        ].map(([tone, label]) => (
          <span key={label} className="inline-flex items-center gap-2"><span className={`h-3 w-3 ${tone}`} /> {label}</span>
        ))}
      </div>
      <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2" data-testid="student-answer-grid">
        {Array.from({ length: questionCount }, (_, index) => index + 1).map((position) => {
          const selected = String(correction?.answers?.[String(position)] || "").trim().toUpperCase();
          const outcome = answerOutcome({ position, selected, answerKey, annulled });
          const meta = outcomeMeta[outcome];
          return (
            <button
              type="button"
              key={position}
              onClick={() => onSelect({ position, outcome })}
              className={`border min-h-16 p-2 text-left hover:-translate-y-0.5 hover:shadow-sm transition-all ${meta.chip}`}
              aria-label={`Questão ${position}: ${meta.label}. Resposta ${selected || "em branco"}`}
              data-testid={`student-answer-${position}`}
            >
              <span className="block text-[10px] font-mono-data uppercase tracking-wider opacity-70">Q{position}</span>
              <span className="block font-display text-2xl font-black mt-1">{selected || "—"}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function liveExamStatus(item) {
  if (item.live_status === "continue") return {
    eyebrow: "prova em andamento", title: "Continuar agora", tone: "text-amber-800",
  };
  if (item.live_status === "available") return {
    eyebrow: "prova ao vivo disponível", title: "Entrar na prova", tone: "text-emerald-700",
  };
  if (item.live_status === "scheduled") return {
    eyebrow: "prova online programada", title: "Ver sala da prova", tone: "text-primary",
  };
  return {
    eyebrow: "aguardando liberação", title: "Acompanhar liberação", tone: "text-muted-foreground",
  };
}

function StudentLiveExams({ items = [] }) {
  if (!items.length) return null;
  return (
    <section className="border-2 border-primary bg-primary/5 p-5 sm:p-6 mb-8" data-testid="student-live-online-exams">
      <div className="flex items-center gap-3">
        <span className="h-11 w-11 bg-primary text-primary-foreground grid place-items-center shrink-0"><Broadcast size={25} weight="fill" /></span>
        <div>
          <div className="label-eyebrow text-primary">provas online</div>
          <h2 className="font-display text-2xl sm:text-3xl font-black">Você tem {items.length} prova(s) ativa(s)</h2>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-3 mt-5">
        {items.map((item) => {
          const status = liveExamStatus(item);
          return <article key={item.session_id} className="border border-primary/40 bg-card p-5 flex flex-col gap-4">
            <div>
              <div className={`label-eyebrow ${status.tone}`}>{status.eyebrow}</div>
              <h3 className="font-display text-2xl font-black mt-1">{item.exam_name}</h3>
              <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-muted-foreground mt-3">
                {item.opens_at && <span><CalendarBlank className="inline mr-1" />abre {new Date(item.opens_at).toLocaleString("pt-BR")}</span>}
                {item.closes_at && <span>entrada até {new Date(item.closes_at).toLocaleString("pt-BR")}</span>}
                {item.total_time_minutes && <span>{item.total_time_minutes} min de prova</span>}
              </div>
              <div className="flex flex-wrap gap-2 mt-3 text-[10px] font-mono-data uppercase tracking-wider">
                {item.camera_required && <span className="border border-amber-500 bg-amber-50 px-2 py-1"><Camera className="inline mr-1" />câmera obrigatória</span>}
                {item.single_screen_required && <span className="border border-amber-500 bg-amber-50 px-2 py-1"><Monitor className="inline mr-1" />um monitor</span>}
                {item.location_required && <span className="border border-amber-500 bg-amber-50 px-2 py-1"><MapPin className="inline mr-1" />localização</span>}
              </div>
            </div>
            <Link to={`/live/${item.join_code}`} className="mt-auto h-11 px-4 inline-flex items-center justify-center bg-primary text-primary-foreground text-xs font-mono-data font-bold uppercase tracking-wider hover:opacity-90" data-testid={`student-live-exam-${item.session_id}`}>
              <PlayCircle size={19} weight="fill" className="mr-2" />{status.title}
            </Link>
          </article>;
        })}
      </div>
    </section>
  );
}

function StudentExams({ semesters }) {
  const [activeKey, setActiveKey] = useState("");
  const [activeAttemptId, setActiveAttemptId] = useState("");
  const [correctionCache, setCorrectionCache] = useState({});
  const [loadingId, setLoadingId] = useState("");
  const [selectedQuestion, setSelectedQuestion] = useState(null);

  const exams = useMemo(() => {
    const rows = [];
    for (const semester of semesters) {
      for (const classGroup of semester.classes || []) {
        for (const examItem of classGroup.exams || []) {
          rows.push({
            ...examItem,
            semester_name: semester.name,
            class_name: classGroup.name,
            school_name: classGroup.school_name,
            row_key: `${semester.id}:${classGroup.id}:${examItem.id}`,
          });
        }
      }
    }
    return rows.sort((a, b) => String(b.exam_date || b.created_at || "").localeCompare(String(a.exam_date || a.created_at || "")));
  }, [semesters]);

  const loadAttempt = async (examItem, attempt) => {
    if (!attempt?.correction_id) return;
    setActiveKey(examItem.row_key);
    setActiveAttemptId(attempt.correction_id);
    setSelectedQuestion(null);
    if (correctionCache[attempt.correction_id]) return;
    setLoadingId(attempt.correction_id);
    try {
      const response = await api.get(`/omr/corrections/${attempt.correction_id}`);
      setCorrectionCache((current) => ({ ...current, [attempt.correction_id]: response.data }));
    } catch (error) {
      toast.error("Não foi possível abrir as respostas: " + fmtError(error));
    } finally {
      setLoadingId("");
    }
  };

  const toggleExam = (examItem) => {
    if (activeKey === examItem.row_key) {
      setActiveKey("");
      setSelectedQuestion(null);
      return;
    }
    loadAttempt(examItem, examItem.latest_attempt || examItem.attempts?.[0]);
  };

  const activeCorrection = correctionCache[activeAttemptId];

  if (!exams.length) {
    return (
      <div className="border border-border bg-card p-12 sm:p-16 text-center">
        <Exam size={36} weight="duotone" className="text-muted-foreground mx-auto mb-4" />
        <h2 className="font-display text-2xl font-black">Nenhuma prova disponível</h2>
        <p className="text-sm text-muted-foreground mt-2">Suas provas aparecerão aqui depois que a correção for concluída.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="student-exams-tab">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <div className="label-eyebrow mb-2">suas provas</div>
          <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Resultados e respostas</h2>
          <p className="text-sm text-muted-foreground mt-2">Abra uma prova para conferir cada resposta. Depois, clique na questão para ver a correção.</p>
        </div>
        <div className="font-mono-data text-xs text-muted-foreground">{exams.length} prova(s)</div>
      </div>

      {exams.map((examItem) => {
        const expanded = activeKey === examItem.row_key;
        const attempt = examItem.latest_attempt;
        const correction = expanded ? correctionCache[activeAttemptId] : null;
        return (
          <article key={examItem.row_key} className={`border bg-card transition-colors ${expanded ? "border-primary" : "border-border"}`}>
            <button
              type="button"
              onClick={() => toggleExam(examItem)}
              className="w-full p-5 sm:p-6 text-left hover:bg-muted/20 transition-colors"
              aria-expanded={expanded}
            >
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="h-12 w-12 bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <Exam size={25} weight="duotone" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap gap-2 mb-2 text-[10px] font-mono-data uppercase tracking-wider text-muted-foreground">
                    <span>{examItem.semester_name}</span>
                    <span>·</span>
                    <span>{examItem.discipline_name || "Global / multidisciplinar"}</span>
                  </div>
                  <h3 className="font-display text-2xl font-black tracking-tight">{examItem.name}</h3>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground mt-2">
                    <span className="inline-flex items-center gap-1"><CalendarBlank size={14} /> {examDate(examItem)}</span>
                    {examItem.class_name && <span>{examItem.class_name}</span>}
                  </div>
                </div>
                {attempt && (
                  <div className="flex items-center gap-5 shrink-0">
                    <div className="text-right">
                      <div className="label-eyebrow mb-1">resultado</div>
                      <div className={`font-display text-3xl font-black ${scoreTone(attempt.score)}`}>{Number(attempt.score || 0).toFixed(1)}%</div>
                      <div className="text-xs text-muted-foreground mt-1">{attempt.hits}/{attempt.total_valid} acertos</div>
                    </div>
                    <CaretDown size={20} weight="bold" className={`transition-transform ${expanded ? "rotate-180 text-primary" : "text-muted-foreground"}`} />
                  </div>
                )}
              </div>
            </button>

            {expanded && (
              <div className="border-t border-border p-5 sm:p-6 bg-muted/10">
                {examItem.attempts?.length > 1 && (
                  <div className="flex flex-wrap items-center gap-2 mb-5">
                    <span className="label-eyebrow mr-2">tentativa</span>
                    {examItem.attempts.map((item, index) => (
                      <button
                        key={item.correction_id}
                        type="button"
                        onClick={() => loadAttempt(examItem, item)}
                        className={`border px-3 py-2 text-xs font-mono-data ${activeAttemptId === item.correction_id ? "border-foreground bg-foreground text-background" : "border-border bg-card hover:border-foreground"}`}
                      >
                        {examItem.attempt_count - index} · {Number(item.score || 0).toFixed(1)}%
                      </button>
                    ))}
                  </div>
                )}
                {loadingId === activeAttemptId && <div className="label-eyebrow py-10 text-center">carregando respostas…</div>}
                {!loadingId && correction && <AnswerGrid correction={correction} onSelect={setSelectedQuestion} />}
                {!loadingId && !correction && <div className="text-sm text-destructive py-6">As respostas desta tentativa não puderam ser carregadas.</div>}
              </div>
            )}
          </article>
        );
      })}

      <StudentQuestionDialog
        selected={selectedQuestion}
        correction={activeCorrection}
        onOpenChange={(open) => { if (!open) setSelectedQuestion(null); }}
      />
    </div>
  );
}

function StudentPerformance({ data }) {
  const {
    summary = {},
    academic_history: academicHistory = [],
    performance_progression: progression = [],
    external_engagement: externalEngagement,
  } = data;

  return (
    <div data-testid="student-performance-tab">
      <div className="mb-6">
        <div className="label-eyebrow mb-2">visão geral</div>
        <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Seu desempenho em um só lugar</h2>
        <p className="text-sm text-muted-foreground mt-2">Acompanhe seu resultado global e a evolução ao longo dos semestres.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
        <div className="border border-border bg-card p-5">
          <Target size={22} weight="duotone" className="text-primary mb-4" />
          <div className="label-eyebrow mb-2">média global</div>
          <div className={`font-display text-4xl font-black ${scoreTone(summary.average_score)}`}>{Number(summary.average_score || 0).toFixed(1)}%</div>
        </div>
        <div className="border border-border bg-card p-5">
          <Exam size={22} weight="duotone" className="text-primary mb-4" />
          <div className="label-eyebrow mb-2">provas realizadas</div>
          <div className="font-display text-4xl font-black">{summary.exams || 0}</div>
        </div>
        <div className="border border-border bg-card p-5">
          <CalendarBlank size={22} weight="duotone" className="text-primary mb-4" />
          <div className="label-eyebrow mb-2">semestres avaliados</div>
          <div className="font-display text-4xl font-black">{summary.semesters || 0}</div>
        </div>
      </div>

      <StudentLearningTimeline progression={progression} engagement={externalEngagement} />

      <section className="border border-border bg-card p-5 sm:p-6 mb-8">
        <div className="flex items-center gap-3 mb-5">
          <CalendarBlank size={23} weight="duotone" className="text-primary" />
          <div>
            <div className="label-eyebrow">semestre a semestre</div>
            <h3 className="font-display text-2xl sm:text-3xl font-black tracking-tight mt-1">Sua evolução por período</h3>
          </div>
        </div>
        {!academicHistory.length ? (
          <div className="text-sm text-muted-foreground">Ainda não há resultados válidos para comparar.</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {academicHistory.map((semester) => (
              <article key={semester.id} className="border border-border p-4">
                <div className="flex items-end justify-between gap-3 border-b border-border pb-3">
                  <div>
                    <div className="label-eyebrow mb-1">semestre</div>
                    <div className="font-mono-data font-bold">{semester.name}</div>
                  </div>
                  <div className={`font-display text-3xl font-black ${scoreTone(semester.average_score)}`}>{Number(semester.average_score || 0).toFixed(1)}%</div>
                </div>
                <div className="space-y-2 mt-3">
                  {(semester.disciplines || []).map((discipline) => (
                    <div key={discipline.id} className="flex items-center justify-between gap-4 text-sm">
                      <span className="text-muted-foreground truncate">{discipline.name}</span>
                      <span className={`font-mono-data font-black ${scoreTone(discipline.average_score)}`}>{Number(discipline.average_score || 0).toFixed(1)}%</span>
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

const studentExamOptions = (semesters = []) => {
  const seen = new Set();
  const rows = [];
  for (const semester of semesters) {
    for (const classGroup of semester.classes || []) {
      for (const examItem of classGroup.exams || []) {
        if (!examItem.id || seen.has(examItem.id)) continue;
        seen.add(examItem.id);
        rows.push({
          id: examItem.id,
          name: examItem.name || "Prova",
          exam_date: examItem.exam_date || examItem.created_at,
          semester_name: semester.name || "Sem semestre",
          class_name: classGroup.name || "Sem turma",
          score: examItem.latest_attempt?.score,
        });
      }
    }
  }
  return rows;
};

function StudentDetailedPerformance({ data }) {
  const examOptions = useMemo(() => studentExamOptions(data.semesters || []), [data.semesters]);
  const allExamIds = useMemo(() => examOptions.map((item) => item.id), [examOptions]);
  const [draftExamIds, setDraftExamIds] = useState(allExamIds);
  const [appliedExamIds, setAppliedExamIds] = useState(allExamIds);
  const [domains, setDomains] = useState(data.academic_domains);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const draftKey = allExamIds.filter((id) => draftExamIds.includes(id)).join(",");
  const appliedKey = allExamIds.filter((id) => appliedExamIds.includes(id)).join(",");
  const appliedExams = examOptions.filter((item) => appliedExamIds.includes(item.id));

  const toggleExam = (examId) => {
    setDraftExamIds((current) => current.includes(examId)
      ? current.filter((item) => item !== examId)
      : [...current, examId]);
  };

  const applyComparison = async () => {
    const selectedIds = allExamIds.filter((id) => draftExamIds.includes(id));
    if (!selectedIds.length) return;
    setLoading(true);
    setError("");
    try {
      if (selectedIds.length === allExamIds.length) {
        setDomains(data.academic_domains);
      } else {
        const response = await api.get(`/students/${data.student.id}/academic-domains`, {
          params: { exam_ids: selectedIds.join(",") },
        });
        setDomains(response.data.academic_domains);
      }
      setAppliedExamIds(selectedIds);
    } catch (requestError) {
      const message = fmtError(requestError);
      setError(message);
      toast.error("Não foi possível comparar as provas: " + message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="student-detailed-performance-tab">
      <div className="mb-6">
        <div className="label-eyebrow mb-2">análise por prova</div>
        <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Desempenho detalhado</h2>
        <p className="text-sm text-muted-foreground mt-2">Escolha uma ou mais provas para comparar seus acertos, erros e respostas em branco por área e conteúdo.</p>
      </div>

      <section className="border border-border bg-card p-5 sm:p-6 mb-6" data-testid="student-detailed-exam-filter">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 border-b border-border pb-4">
          <div>
            <div className="flex items-center gap-2 label-eyebrow"><FunnelSimple size={17} weight="duotone" className="text-primary" /> filtro de comparação</div>
            <h3 className="font-display text-2xl font-black tracking-tight mt-1">Quais provas você quer analisar?</h3>
            <p className="text-xs text-muted-foreground mt-2">Você pode selecionar apenas uma prova ou combinar várias no mesmo mapa de desempenho.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <button type="button" className="underline" onClick={() => setDraftExamIds(allExamIds)}>Selecionar todas</button>
            <button type="button" className="underline" onClick={() => setDraftExamIds([])}>Limpar</button>
            <span className="border border-border px-3 py-2 font-mono-data font-bold">{draftExamIds.length} de {examOptions.length} selecionada(s)</span>
          </div>
        </div>

        {!examOptions.length ? (
          <div className="border border-dashed border-border p-8 mt-5 text-sm text-muted-foreground">Nenhuma prova realizada está disponível para comparação.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-5 max-h-72 overflow-y-auto pr-1">
            {examOptions.map((examItem) => (
              <label key={examItem.id} className={`border p-3 flex items-start gap-3 cursor-pointer transition-colors ${draftExamIds.includes(examItem.id) ? "border-primary bg-primary/5" : "border-border hover:border-foreground"}`}>
                <input
                  type="checkbox"
                  className="mt-1 accent-primary"
                  checked={draftExamIds.includes(examItem.id)}
                  onChange={() => toggleExam(examItem.id)}
                  data-testid={`student-detailed-exam-${examItem.id}`}
                />
                <span className="min-w-0 flex-1">
                  <span className="block font-semibold leading-snug">{examItem.name}</span>
                  <span className="block text-xs text-muted-foreground mt-1">{examItem.semester_name} · {examItem.class_name} · {examDate(examItem)}</span>
                </span>
                {examItem.score != null && <span className={`font-mono-data font-black text-sm ${scoreTone(examItem.score)}`}>{Number(examItem.score).toFixed(1)}%</span>}
              </label>
            ))}
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-border mt-5 pt-4">
          <div className="text-xs text-muted-foreground">
            {draftExamIds.length ? `${draftExamIds.length} prova(s) pronta(s) para comparar.` : "Selecione pelo menos uma prova."}
          </div>
          <button
            type="button"
            onClick={applyComparison}
            disabled={loading || !draftExamIds.length || draftKey === appliedKey}
            className="border border-primary bg-primary text-primary-foreground px-5 py-3 text-xs font-mono-data font-bold uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="student-detailed-compare"
          >
            {loading ? "Calculando comparação…" : "Atualizar comparação"}
          </button>
        </div>
        {error && <div className="border border-destructive bg-destructive/5 p-3 mt-4 text-sm text-destructive">{error}</div>}
      </section>

      <div className="border border-primary/30 bg-primary/5 p-4 mb-6 text-sm" data-testid="student-detailed-applied-summary">
        <strong>{appliedExams.length} prova(s) na análise atual.</strong>
        <span className="text-muted-foreground"> {appliedExams.map((item) => item.name).join(" · ") || "Nenhuma prova selecionada"}</span>
      </div>

      <AcademicDomainChart
        domains={domains}
        eyebrow="áreas e conteúdos"
        title="Onde você está indo bem e onde pode melhorar"
        description="Os resultados abaixo consideram somente as provas selecionadas no filtro acima. Abra uma área para ver os conteúdos e as questões comentadas."
        individual
      />
    </div>
  );
}

function StudentCohortComparison({ data }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [semesterId, setSemesterId] = useState("");
  const [rankingScope, setRankingScope] = useState("class");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    api.get(`/students/${data.student.id}/cohort-comparison`)
      .then((response) => {
        if (!alive) return;
        setResult(response.data);
        setSemesterId((current) => current || response.data.semesters?.[0]?.id || "");
      })
      .catch((requestError) => {
        if (!alive) return;
        const message = fmtError(requestError);
        setError(message);
        toast.error("Não foi possível carregar a comparação: " + message);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [data.student.id]);

  const semesters = result?.semesters || [];
  const semester = semesters.find((item) => item.id === semesterId) || semesters[0];
  const positionByScope = Object.fromEntries((semester?.positions || []).map((item) => [item.scope, item]));
  const scopeOptions = [
    ["class", "Turma"],
    ["school", "Escola"],
    ["overall", "Geral"],
  ];

  return (
    <div data-testid="student-cohort-comparison-tab">
      <div className="mb-6">
        <div className="label-eyebrow mb-2">comparação entre pares</div>
        <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Comparação e ranking</h2>
        <p className="text-sm text-muted-foreground mt-2">Veja sua posição na turma, na escola e entre os alunos que realizaram as mesmas provas, sempre separada por semestre.</p>
      </div>

      {loading && <div className="border border-border bg-card p-14 text-center label-eyebrow">calculando suas coortes…</div>}
      {!loading && error && <div className="border border-destructive bg-destructive/5 p-5 text-sm text-destructive">{error}</div>}
      {!loading && !error && !semesters.length && (
        <div className="border border-dashed border-border bg-card p-12 text-center text-sm text-muted-foreground">Ainda não há resultados suficientes para montar uma comparação entre pares.</div>
      )}

      {!loading && !error && semester && (
        <>
          <section className="border border-border bg-card p-5 sm:p-6 mb-6">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div>
                <div className="label-eyebrow">período analisado</div>
                <h3 className="font-display text-2xl font-black mt-1">Escolha o semestre</h3>
                <p className="text-xs text-muted-foreground mt-2">Cada período usa somente as provas realizadas naquele semestre.</p>
              </div>
              <select
                value={semester.id}
                onChange={(event) => setSemesterId(event.target.value)}
                className="border border-border bg-background px-4 py-3 text-sm font-mono-data min-w-64"
                data-testid="student-cohort-semester"
              >
                {semesters.map((item) => <option key={item.id} value={item.id}>{item.name} · {item.exam_count} prova(s)</option>)}
              </select>
            </div>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6" data-testid="student-cohort-positions">
            {scopeOptions.map(([scope, label]) => {
              const position = positionByScope[scope];
              return (
                <article key={scope} className="border border-border bg-card p-5">
                  <div className="flex items-center justify-between gap-3">
                    <div className="label-eyebrow">{label}</div>
                    <UsersThree size={21} weight="duotone" className="text-primary" />
                  </div>
                  {position ? (
                    <>
                      <div className="font-display text-4xl font-black mt-4">{position.rank}º <span className="text-lg text-muted-foreground">/{position.total}</span></div>
                      <div className="text-sm font-semibold mt-2">{position.label}</div>
                      <div className="grid grid-cols-2 gap-2 border-t border-border mt-4 pt-3 text-xs">
                        <div><span className="text-muted-foreground block">Você</span><strong>{Number(position.score).toFixed(1)}%</strong></div>
                        <div><span className="text-muted-foreground block">Média do grupo</span><strong>{Number(position.cohort_average).toFixed(1)}%</strong></div>
                      </div>
                      <div className={`font-mono-data text-xs font-bold mt-3 ${Number(position.difference_from_average) >= 0 ? "text-[hsl(var(--success))]" : "text-destructive"}`}>
                        {Number(position.difference_from_average) > 0 ? "+" : ""}{Number(position.difference_from_average).toFixed(1)} p.p. em relação à média
                      </div>
                    </>
                  ) : <div className="text-sm text-muted-foreground mt-5">Sem pares suficientes neste recorte.</div>}
                </article>
              );
            })}
          </div>

          <section className="border border-border bg-card mb-6" data-testid="student-discipline-ranking">
            <div className="p-5 sm:p-6 border-b border-border flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 label-eyebrow"><Trophy size={18} weight="duotone" className="text-primary" /> ranking por disciplina</div>
                <h3 className="font-display text-2xl sm:text-3xl font-black mt-1">Sua posição em cada área</h3>
                <p className="text-xs text-muted-foreground mt-2">Nas provas multidisciplinares, as disciplinas são identificadas pela área cadastrada em cada questão.</p>
              </div>
              <div className="flex flex-wrap gap-1" role="group" aria-label="Coorte do ranking por disciplina">
                {scopeOptions.map(([scope, label]) => (
                  <button
                    key={scope}
                    type="button"
                    onClick={() => setRankingScope(scope)}
                    aria-pressed={rankingScope === scope}
                    className={`border px-4 py-2 text-xs font-mono-data uppercase tracking-wider ${rankingScope === scope ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-foreground"}`}
                    data-testid={`student-discipline-scope-${scope}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {(semester.disciplines || []).length ? (
              <div className="overflow-x-auto">
                <div className="min-w-[760px]">
                  <div className="grid grid-cols-[1fr_110px_110px_130px_140px] gap-3 px-5 py-3 bg-muted/30 label-eyebrow">
                    <div>Disciplina / área</div><div>Você</div><div>Média</div><div>Diferença</div><div>Posição</div>
                  </div>
                  {semester.disciplines.map((discipline) => {
                    const ranking = discipline.rankings?.[rankingScope];
                    return (
                      <div key={discipline.id} className="grid grid-cols-[1fr_110px_110px_130px_140px] gap-3 px-5 py-4 border-t border-border items-center text-sm">
                        <div><div className="font-semibold">{discipline.name}</div><div className="text-xs text-muted-foreground mt-1">{discipline.exam_count} prova(s)</div></div>
                        <div className={`font-mono-data font-black ${scoreTone(discipline.score)}`}>{Number(discipline.score).toFixed(1)}%</div>
                        <div className="font-mono-data font-bold">{ranking ? `${Number(ranking.cohort_average).toFixed(1)}%` : "—"}</div>
                        <div className={`font-mono-data font-bold ${ranking && Number(ranking.difference_from_average) >= 0 ? "text-[hsl(var(--success))]" : ranking ? "text-destructive" : "text-muted-foreground"}`}>
                          {ranking ? `${Number(ranking.difference_from_average) > 0 ? "+" : ""}${Number(ranking.difference_from_average).toFixed(1)} p.p.` : "—"}
                        </div>
                        <div className="font-display text-xl font-black">{ranking ? `${ranking.rank}º / ${ranking.total}` : "—"}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : <div className="p-10 text-sm text-muted-foreground text-center">As provas deste semestre ainda não possuem disciplinas ou áreas cadastradas.</div>}
          </section>

          <div className="border border-border bg-muted/20 p-4 text-xs text-muted-foreground">
            <strong className="text-foreground">Privacidade:</strong> {result.privacy}
          </div>
        </>
      )}
    </div>
  );
}

export default function StudentPortalPage({ data }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const activeTab = ["performance", "detailed", "cohorts"].includes(requestedTab) ? requestedTab : "exams";
  const student = data.student || {};
  const firstName = String(student.name || "Aluno").trim().split(/\s+/)[0];

  const selectTab = (tab) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", tab);
    setSearchParams(next, { replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <main className="max-w-[1180px] mx-auto px-4 sm:px-8 py-8 sm:py-10">
      <header className="mb-7">
        <div className="label-eyebrow mb-2">área do aluno</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">Olá, {firstName}.</h1>
        <p className="text-sm text-muted-foreground mt-2">Veja suas provas e acompanhe sua evolução.</p>
      </header>

      <StudentLiveExams items={data.live_online_exams || []} />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 border border-border bg-card mb-8" role="tablist" aria-label="Área do aluno">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "exams"}
          onClick={() => selectTab("exams")}
          className={`min-h-16 px-4 flex items-center justify-center gap-2 text-sm font-mono-data font-bold uppercase tracking-wider transition-colors ${activeTab === "exams" ? "bg-foreground text-background" : "hover:bg-muted/30"}`}
          data-testid="student-tab-exams"
        >
          <Exam size={20} weight="duotone" /> Provas
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "performance"}
          onClick={() => selectTab("performance")}
          className={`min-h-16 px-4 flex items-center justify-center gap-2 text-sm font-mono-data font-bold uppercase tracking-wider transition-colors ${activeTab === "performance" ? "bg-foreground text-background" : "hover:bg-muted/30"}`}
          data-testid="student-tab-performance"
        >
          <ChartBar size={20} weight="duotone" /> Desempenho geral
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "detailed"}
          onClick={() => selectTab("detailed")}
          className={`min-h-16 px-4 flex items-center justify-center gap-2 text-sm font-mono-data font-bold uppercase tracking-wider transition-colors ${activeTab === "detailed" ? "bg-foreground text-background" : "hover:bg-muted/30"}`}
          data-testid="student-tab-detailed-performance"
        >
          <FunnelSimple size={20} weight="duotone" /> Desempenho detalhado
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "cohorts"}
          onClick={() => selectTab("cohorts")}
          className={`min-h-16 px-4 flex items-center justify-center gap-2 text-sm font-mono-data font-bold uppercase tracking-wider transition-colors ${activeTab === "cohorts" ? "bg-foreground text-background" : "hover:bg-muted/30"}`}
          data-testid="student-tab-cohorts"
        >
          <UsersThree size={20} weight="duotone" /> Comparação e ranking
        </button>
      </div>

      {activeTab === "exams" && <StudentExams semesters={data.semesters || []} />}
      {activeTab === "performance" && <StudentPerformance data={data} />}
      {activeTab === "detailed" && <StudentDetailedPerformance data={data} />}
      {activeTab === "cohorts" && <StudentCohortComparison data={data} />}

      <div className="mt-10 border-t border-border pt-5 text-xs text-muted-foreground flex items-center gap-2">
        <Books size={16} /> Cada questão comentada usa o conteúdo preservado no momento em que a prova foi aplicada.
        <ArrowRight size={14} />
      </div>
    </main>
  );
}
